import { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import {
  ArrowLeft,
  ArrowUpRight,
  BadgeCheck,
  CalendarDays,
  ChevronRight,
  Clock,
  Download,
  FileText,
  Film,
  Globe,
  GraduationCap,
  Heart,
  Instagram,
  Languages,
  Linkedin,
  MapPin,
  Play,
  Quote,
  Send,
  X,
} from 'lucide-react'

import LoadingSpinner from '../components/LoadingSpinner'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import api from '../services/api'
import { getUserData } from '../utils/userData'
import { trackEvent } from '../utils/mixpanel'
import { cn } from '../lib/utils'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

const FORMAT_TONE = {
  online: 'text-sky-700 bg-sky-50 border-sky-100',
  'in-person': 'text-primary-800 bg-primary-50 border-primary-100',
  hybrid: 'text-violet-700 bg-violet-50 border-violet-100',
}

const RESOURCE_TYPE_META = {
  pdf: { icon: FileText, label: 'PDF' },
  doc: { icon: FileText, label: 'Document' },
  video: { icon: Film, label: 'Video' },
  link: { icon: Globe, label: 'Link' },
}

const resolveMediaUrl = (url) => {
  if (!url) return ''
  return url.startsWith('http') ? url : `${API_BASE_URL}${url}`
}

const SectionHeading = ({ eyebrow, title, action }) => (
  <div className="mb-5 flex items-end justify-between gap-4">
    <div>
      {eyebrow && (
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-600">
          {eyebrow}
        </p>
      )}
      <h2 className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">{title}</h2>
    </div>
    {action}
  </div>
)

const StatPill = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2.5 rounded-xl border border-primary-100/80 bg-white/70 px-3 py-2 backdrop-blur-sm">
    <Icon className="h-4 w-4 text-primary-700" aria-hidden="true" />
    <div className="leading-tight">
      <div className="text-sm font-semibold text-gray-900">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
    </div>
  </div>
)

const SocialIconLink = ({ href, label, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    className="group inline-flex h-9 w-9 items-center justify-center rounded-full border border-primary-100 bg-white/80 text-primary-700 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800 hover:shadow-md"
  >
    {children}
  </a>
)

const FacilitatorDetail = () => {
  const { slug } = useParams()
  const shouldReduceMotion = useReducedMotion()

  const [facilitator, setFacilitator] = useState(null)
  const [events, setEvents] = useState({ upcoming: [], past: [] })
  const [consultations, setConsultations] = useState([])
  const [classes, setClasses] = useState([])
  const [testimonials, setTestimonials] = useState([])
  const [gallery, setGallery] = useState([])
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [selectedListing, setSelectedListing] = useState(null)
  const [interestKind, setInterestKind] = useState('consultation')
  const [interestForm, setInterestForm] = useState({ name: '', email: '', phone: '', message: '' })
  const [submittingInterest, setSubmittingInterest] = useState(false)
  const [interestSuccess, setInterestSuccess] = useState(false)
  const [interestError, setInterestError] = useState(null)

  const [activeTab, setActiveTab] = useState('about')
  const [lightbox, setLightbox] = useState(null)

  useEffect(() => {
    trackEvent('Page Viewed', { page: 'Facilitator Detail', slug })
  }, [slug])

  useEffect(() => {
    const fetchFacilitator = async () => {
      try {
        setLoading(true)
        setError(null)
        const response = await api.get(`/api/facilitators/profile/${slug}`)
        if (response.data.success) setFacilitator(response.data.data)
      } catch (err) {
        console.error('Error fetching facilitator:', err)
        setError(err.response?.status === 404 ? 'Facilitator not found' : 'Failed to load facilitator profile')
      } finally {
        setLoading(false)
      }
    }

    const fetchEvents = async () => {
      try {
        const response = await api.get(`/api/facilitators/profile/${slug}/events`)
        if (response.data.success) setEvents(response.data.data)
      } catch (err) {
        console.error('Error fetching facilitator events:', err)
      }
    }

    const fetchConsultations = async () => {
      try {
        const response = await api.get(`/api/facilitators/profile/${slug}/consultations`)
        if (response.data.success) setConsultations(response.data.data)
      } catch (err) {
        console.error('Error fetching consultations:', err)
      }
    }

    const fetchClasses = async () => {
      try {
        const response = await api.get(`/api/facilitators/profile/${slug}/classes`)
        if (response.data.success) setClasses(response.data.data)
      } catch (err) {
        console.error('Error fetching classes:', err)
      }
    }

    const fetchTestimonials = async () => {
      try {
        const response = await api.get(`/api/facilitators/profile/${slug}/testimonials`)
        if (response.data.success) setTestimonials(response.data.data)
      } catch (err) {
        console.error('Error fetching testimonials:', err)
      }
    }

    const fetchGallery = async () => {
      try {
        const response = await api.get(`/api/facilitators/profile/${slug}/gallery`)
        if (response.data.success) setGallery(response.data.data)
      } catch (err) {
        console.error('Error fetching gallery:', err)
      }
    }

    const fetchResources = async () => {
      try {
        const response = await api.get(`/api/facilitators/profile/${slug}/resources`)
        if (response.data.success) setResources(response.data.data)
      } catch (err) {
        console.error('Error fetching resources:', err)
      }
    }

    if (slug) {
      fetchFacilitator()
      fetchEvents()
      fetchConsultations()
      fetchClasses()
      fetchTestimonials()
      fetchGallery()
      fetchResources()
    }
  }, [slug])

  const openInterestModal = (listing, kind = 'consultation') => {
    const userData = getUserData()
    setInterestForm({
      name: userData?.name || '',
      email: userData?.email || '',
      phone: userData?.phone || '',
      message: '',
    })
    setInterestKind(kind)
    setSelectedListing(listing)
    setInterestSuccess(false)
    setInterestError(null)
  }

  const closeInterestModal = () => {
    setSelectedListing(null)
    setInterestSuccess(false)
    setInterestError(null)
  }

  const handleInterestSubmit = async (e) => {
    e.preventDefault()
    if (!interestForm.name.trim() || !interestForm.email.trim()) return
    try {
      setSubmittingInterest(true)
      setInterestError(null)
      const endpoint =
        interestKind === 'class'
          ? `/api/facilitators/profile/${slug}/classes/${selectedListing.id}/interest`
          : `/api/facilitators/profile/${slug}/consultations/${selectedListing.id}/interest`
      await api.post(endpoint, interestForm)
      trackEvent(interestKind === 'class' ? 'Class Interest Submitted' : 'Consultation Interest Submitted', {
        listing: selectedListing.title,
        facilitator: facilitator?.name,
      })
      setInterestSuccess(true)
    } catch (err) {
      setInterestError(err.response?.data?.message || 'Failed to submit. Please try again.')
    } finally {
      setSubmittingInterest(false)
    }
  }

  const primaryTag = useMemo(() => facilitator?.tags?.find((t) => t.tagType === 'primary'), [facilitator])
  const title = primaryTag?.name || facilitator?.tags?.[0]?.name || 'Wellness Facilitator'

  const heroStats = useMemo(() => {
    const eventsHosted = events.upcoming.length + events.past.length
    const stats = []
    if (eventsHosted > 0) {
      stats.push({ label: 'Events hosted', value: String(eventsHosted), icon: CalendarDays })
    }
    if (facilitator?.yearsGuiding) {
      stats.push({ label: 'Years guiding', value: String(facilitator.yearsGuiding), icon: Heart })
    }
    return stats
  }, [events, facilitator])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !facilitator) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-4">
        <div className="max-w-md text-center">
          <h1 className="mb-3 text-2xl font-semibold text-gray-900">{error || 'Facilitator not found'}</h1>
          <p className="mb-6 text-sm text-gray-600">
            The facilitator profile you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <Button asChild className="rounded-full bg-primary-700 hover:bg-primary-800">
            <Link to="/facilitators">View all facilitators</Link>
          </Button>
        </div>
      </div>
    )
  }

  const profilePictureUrl = facilitator.profilePicture
    ? `${API_BASE_URL}${facilitator.profilePicture}`
    : null

  const heroAnim = shouldReduceMotion
    ? {}
    : { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, ease: 'easeOut' } }

  return (
    <div className="min-h-screen bg-cream">
      {/* Ambient background */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 -z-0 h-[420px] overflow-hidden">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-primary-200/40 blur-3xl" />
        <div className="absolute right-0 top-20 h-64 w-64 rounded-full bg-cream-300/60 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-5xl px-4 pb-16 pt-6 sm:px-6 sm:pt-10">
        {/* Back link */}
        <motion.div {...heroAnim}>
          <Link
            to="/facilitators"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 transition-colors hover:text-primary-900"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>All facilitators</span>
          </Link>
        </motion.div>

        {/* Hero card */}
        <motion.section
          {...heroAnim}
          transition={{ duration: 0.5, delay: 0.05, ease: 'easeOut' }}
          className="mt-4 overflow-hidden rounded-2xl border border-primary-100/70 bg-white/85 shadow-sm backdrop-blur-sm"
        >
          <div className="grid gap-5 p-5 sm:grid-cols-[auto,1fr] sm:p-6">
            {/* Avatar */}
            <motion.div
              initial={shouldReduceMotion ? false : { scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.45, delay: 0.1, ease: 'easeOut' }}
              className="mx-auto sm:mx-0"
            >
              <Avatar className="h-24 w-24 ring-4 ring-white shadow-md sm:h-28 sm:w-28">
                {profilePictureUrl && (
                  <AvatarImage
                    src={profilePictureUrl}
                    alt={`${facilitator.name} profile`}
                    className="object-cover"
                  />
                )}
                <AvatarFallback className="bg-primary-100 text-3xl font-semibold text-primary-700">
                  {facilitator.name?.charAt(0)?.toUpperCase() || '?'}
                </AvatarFallback>
              </Avatar>
            </motion.div>

            {/* Name & meta */}
            <div className="text-center sm:text-left">
              <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                <h1 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
                  {facilitator.name}
                </h1>
                <Badge variant="verified" className="gap-1">
                  <BadgeCheck className="h-3 w-3" />
                  Verified
                </Badge>
              </div>
              <p className="mt-1 text-sm font-medium text-primary-700 sm:text-base">{title}</p>

              <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-gray-600 sm:justify-start">
                {facilitator.location && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3 text-primary-600" />
                    {facilitator.location}
                  </span>
                )}
                {facilitator.languages?.length > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <Languages className="h-3 w-3 text-primary-600" />
                    {facilitator.languages.join(' · ')}
                  </span>
                )}

              </div>

              {/* Tags */}
              {facilitator.tags?.length > 0 && (
                <div className="mt-3 flex flex-wrap justify-center gap-1.5 sm:justify-start">
                  {facilitator.tags.map((tag) => (
                    <Badge
                      key={tag.id}
                      variant={tag.tagType === 'primary' ? 'secondary' : 'outline'}
                      className="text-[11px] font-medium"
                    >
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Actions row */}
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                <Button
                  size="sm"
                  onClick={() => setActiveTab('classes')}
                  className="h-9 rounded-full bg-primary-700 px-4 text-xs font-semibold text-white shadow-sm hover:bg-primary-800"
                >
                  Book a session
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </Button>
                {facilitator.instagramId && (
                  <SocialIconLink
                    href={`https://instagram.com/${facilitator.instagramId.replace('@', '')}`}
                    label="Instagram"
                  >
                    <Instagram className="h-4 w-4" />
                  </SocialIconLink>
                )}
                {facilitator.linkedInUrl && (
                  <SocialIconLink href={facilitator.linkedInUrl} label="LinkedIn">
                    <Linkedin className="h-4 w-4" />
                  </SocialIconLink>
                )}
                {facilitator.website && (
                  <SocialIconLink
                    href={facilitator.website.startsWith('http') ? facilitator.website : `https://${facilitator.website}`}
                    label="Website"
                  >
                    <Globe className="h-4 w-4" />
                  </SocialIconLink>
                )}
              </div>
            </div>
          </div>

          {/* Stat strip */}
          {heroStats.length > 0 && (
            <div className="border-t border-primary-100/70 bg-gradient-to-r from-cream/60 via-white/40 to-primary-50/50 px-5 py-3 sm:px-6">
              <div className="flex flex-wrap items-center gap-2.5">
                {heroStats.map((stat) => (
                  <StatPill key={stat.label} {...stat} />
                ))}
              </div>
            </div>
          )}
        </motion.section>

        {/* Tabs */}
        <motion.div
          {...heroAnim}
          transition={{ duration: 0.5, delay: 0.15, ease: 'easeOut' }}
          className="mt-6"
        >
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="overflow-x-auto pb-1">
              <TabsList className="inline-flex h-auto w-max gap-1 rounded-full border border-primary-100 bg-white/70 p-1 text-primary-800 backdrop-blur-sm shadow-sm">
                {[
                  ['about', 'About'],
                  ['classes', 'Classes'],
                  ['events', 'Events'],
                  ['gallery', 'Gallery'],
                  ['testimonials', 'Testimonials'],
                  ['resources', 'Resources'],
                ].map(([value, label]) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    className="rounded-full px-3.5 py-1.5 text-sm font-medium text-primary-800 transition-all data-[state=active]:bg-primary-700 data-[state=active]:text-white data-[state=active]:shadow"
                  >
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {/* About */}
            <TabsContent value="about" className="mt-5">
              <div className="grid gap-5 lg:grid-cols-[1.4fr,1fr]">
                {facilitator.bio && (
                  <Card className="rounded-2xl border-primary-100/70 bg-white/85 shadow-sm backdrop-blur-sm">
                    <CardContent className="p-5 sm:p-6">
                      <SectionHeading eyebrow="The story" title="About" />
                      <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700 sm:text-[15px]">
                        {facilitator.bio}
                      </p>
                    </CardContent>
                  </Card>
                )}

                <Card className="rounded-2xl border-primary-100/70 bg-white/85 shadow-sm backdrop-blur-sm">
                  <CardContent className="p-5 sm:p-6">
                    <SectionHeading
                      eyebrow="Verified"
                      title="Certifications"
                      action={
                        facilitator?.certifications?.some((c) => c.verificationStatus === 'verified') ? (
                          <span className="hidden text-[11px] font-medium uppercase tracking-wider text-primary-700 sm:inline-flex sm:items-center sm:gap-1">
                            <BadgeCheck className="h-3.5 w-3.5" /> Reviewed by Wellness Loop
                          </span>
                        ) : null
                      }
                    />
                    {facilitator?.certifications?.length > 0 ? (
                      <ul className="space-y-2.5">
                        {facilitator.certifications.map((cert, i) => (
                          <motion.li
                            key={cert.id}
                            initial={shouldReduceMotion ? false : { opacity: 0, x: -8 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.35, delay: i * 0.05 }}
                            className="flex items-start justify-between gap-3 rounded-xl border border-primary-100/80 bg-white/70 px-3.5 py-2.5"
                          >
                            <div className="flex items-start gap-2.5">
                              <GraduationCap className="mt-0.5 h-4 w-4 shrink-0 text-primary-700" />
                              <div className="leading-tight">
                                <div className="text-sm font-medium text-gray-900">{cert.name}</div>
                                <div className="text-[11px] text-gray-500">{cert.issuer}</div>
                              </div>
                            </div>
                            {cert.verificationStatus === 'verified' ? (
                              <Badge variant="verified" className="shrink-0">
                                <BadgeCheck className="h-3 w-3" /> Verified
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="shrink-0">
                                In review
                              </Badge>
                            )}
                          </motion.li>
                        ))}
                      </ul>
                    ) : (
                      <div className="rounded-xl border border-dashed border-primary-200 bg-primary-50/40 p-6 text-center">
                        <GraduationCap className="mx-auto mb-2 h-5 w-5 text-primary-600" />
                        <p className="text-sm text-gray-700">No certifications yet.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Classes */}
            <TabsContent value="classes" className="mt-5">
              <Card className="rounded-2xl border-primary-100/70 bg-white/85 shadow-sm backdrop-blur-sm">
                <CardContent className="p-5 sm:p-6">
                  <SectionHeading
                    eyebrow="Weekly offerings"
                    title="Classes I'm hosting"
                    action={
                      <span className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
                        Updated weekly
                      </span>
                    }
                  />
                  {classes.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-primary-200 bg-primary-50/40 p-6 text-center">
                      <CalendarDays className="mx-auto mb-2 h-5 w-5 text-primary-600" />
                      <p className="text-sm text-gray-700">No classes listed yet.</p>
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {classes.map((cls, i) => (
                        <motion.div
                          key={cls.id}
                          initial={shouldReduceMotion ? false : { opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, amount: 0.2 }}
                          whileHover={shouldReduceMotion ? undefined : { y: -3 }}
                          transition={{ duration: 0.35, delay: i * 0.05, ease: 'easeOut' }}
                          className="group relative overflow-hidden rounded-xl border border-primary-100/80 bg-gradient-to-br from-primary-50 to-cream p-4 transition-shadow hover:shadow-md"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="text-sm font-semibold tracking-tight text-gray-900">{cls.title}</h3>
                            {cls.level && <Badge variant="format" className="shrink-0">{cls.level}</Badge>}
                          </div>
                          <div className="mt-3 space-y-1 text-xs text-gray-700">
                            {cls.schedule && (
                              <div className="flex items-center gap-1.5">
                                <CalendarDays className="h-3 w-3 text-primary-700" /> {cls.schedule}
                              </div>
                            )}
                            {cls.timeSlot && (
                              <div className="flex items-center gap-1.5">
                                <Clock className="h-3 w-3 text-primary-700" /> {cls.timeSlot}
                              </div>
                            )}
                          </div>
                          <Separator className="my-3 bg-primary-100/70" />
                          <div className="flex items-center justify-between">
                            <div>
                              {cls.fee && <div className="text-sm font-semibold text-gray-900">{cls.fee}</div>}
                              {cls.spotsText && <div className="text-[11px] text-primary-700">{cls.spotsText}</div>}
                            </div>
                            <button
                              type="button"
                              onClick={() => openInterestModal(cls, 'class')}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary-700 text-white shadow-sm transition-all hover:bg-primary-800 group-hover:scale-105"
                              aria-label={`Reserve ${cls.title}`}
                            >
                              <ArrowUpRight className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Consultations sit alongside classes */}
                  {consultations.length > 0 && (
                    <>
                      <Separator className="my-6 bg-primary-100/70" />
                      <SectionHeading
                        eyebrow="1-on-1"
                        title="Consultations"
                        action={
                          consultations.length > 3 && (
                            <Link
                              to={`/facilitator/${slug}/consultations`}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:text-primary-900"
                            >
                              View all <ChevronRight className="h-3 w-3" />
                            </Link>
                          )
                        }
                      />
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {consultations.slice(0, 3).map((listing) => (
                          <motion.div
                            key={listing.id}
                            whileHover={shouldReduceMotion ? undefined : { y: -3 }}
                            transition={{ duration: 0.2 }}
                            className="flex flex-col overflow-hidden rounded-xl border border-primary-100/80 bg-white/85 shadow-sm transition-shadow hover:shadow-md"
                          >
                            {listing.image && (
                              <div className="h-28 overflow-hidden bg-primary-50">
                                <img
                                  src={listing.image.startsWith('http') ? listing.image : `${API_BASE_URL}${listing.image}`}
                                  alt={listing.title}
                                  className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                                  onError={(e) => {
                                    e.target.style.display = 'none'
                                  }}
                                />
                              </div>
                            )}
                            <div className="flex flex-1 flex-col p-3.5">
                              <div className="mb-2 flex flex-wrap gap-1.5">
                                <span
                                  className={cn(
                                    'rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize',
                                    FORMAT_TONE[listing.format] || FORMAT_TONE.online
                                  )}
                                >
                                  {listing.format}
                                </span>
                                {listing.fee && (
                                  <Badge variant="warning" className="text-[10px]">
                                    {listing.fee.toLowerCase() === 'free' ? 'FREE' : listing.fee}
                                  </Badge>
                                )}
                              </div>
                              <h4 className="text-sm font-semibold leading-tight text-gray-900">{listing.title}</h4>
                              {listing.description && (
                                <p className="mt-1 line-clamp-2 text-xs text-gray-600">{listing.description}</p>
                              )}
                              {listing.duration && (
                                <p className="mt-2 flex items-center gap-1 text-[11px] text-gray-500">
                                  <Clock className="h-3 w-3" /> {listing.duration}
                                </p>
                              )}
                              <Button
                                onClick={() => openInterestModal(listing)}
                                size="sm"
                                className="mt-3 h-8 rounded-full bg-primary-700 text-xs font-semibold hover:bg-primary-800"
                              >
                                I&apos;m interested
                              </Button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Events */}
            <TabsContent value="events" className="mt-5">
              <Card className="rounded-2xl border-primary-100/70 bg-white/85 shadow-sm backdrop-blur-sm">
                <CardContent className="p-5 sm:p-6">
                  <SectionHeading
                    eyebrow="Calendar"
                    title="Upcoming events"
                    action={
                      events.upcoming.length > 0 && (
                        <Link
                          to={`/facilitator/${slug}/events`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:text-primary-900"
                        >
                          All events <ChevronRight className="h-3 w-3" />
                        </Link>
                      )
                    }
                  />
                  {events.upcoming.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-primary-200 bg-primary-50/40 p-6 text-center">
                      <CalendarDays className="mx-auto mb-2 h-5 w-5 text-primary-600" />
                      <p className="text-sm text-gray-700">No upcoming events right now.</p>
                      <p className="text-xs text-gray-500">Follow on Instagram for new offerings.</p>
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {events.upcoming.map((event, i) => (
                        <motion.div
                          key={event.id}
                          initial={shouldReduceMotion ? false : { opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, amount: 0.2 }}
                          transition={{ duration: 0.35, delay: i * 0.04 }}
                        >
                          <Link to="/events" className="block group">
                            <div className="overflow-hidden rounded-xl border border-primary-100/80 bg-white/85 transition-shadow hover:shadow-md">
                              {event.image && (
                                <div className="h-32 overflow-hidden bg-primary-50">
                                  <img
                                    src={event.image.startsWith('http') ? event.image : `${API_BASE_URL}${event.image}`}
                                    alt={event.name}
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    onError={(e) => {
                                      e.target.style.display = 'none'
                                    }}
                                  />
                                </div>
                              )}
                              <div className="p-3.5">
                                <div className="mb-2 flex flex-wrap gap-1.5">
                                  <span
                                    className={cn(
                                      'rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize',
                                      FORMAT_TONE[event.format] || FORMAT_TONE.online
                                    )}
                                  >
                                    {event.format}
                                  </span>
                                  {event.fee?.toLowerCase() === 'free' && (
                                    <Badge variant="warning" className="text-[10px]">FREE</Badge>
                                  )}
                                </div>
                                <h3 className="text-sm font-semibold leading-tight text-gray-900 group-hover:text-primary-700">
                                  {event.name}
                                </h3>
                                <div className="mt-2 space-y-1 text-xs text-gray-600">
                                  <div className="flex items-center gap-1.5">
                                    <CalendarDays className="h-3 w-3 text-primary-700" />
                                    {new Date(event.eventDate).toLocaleDateString('en-IN', {
                                      weekday: 'short',
                                      month: 'short',
                                      day: 'numeric',
                                    })}
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <Clock className="h-3 w-3 text-primary-700" />
                                    {new Date(event.eventDate).toLocaleTimeString('en-IN', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                    {event.endDate &&
                                      ` – ${new Date(event.endDate).toLocaleTimeString('en-IN', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}`}
                                  </div>
                                  {(event.space || event.location) && (
                                    <div className="flex items-center gap-1.5">
                                      <MapPin className="h-3 w-3 text-primary-700" />
                                      {event.space ? `${event.space.name}${event.space.city ? `, ${event.space.city}` : ''}` : event.location}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {events.past.length > 0 && (
                    <>
                      <Separator className="my-6 bg-primary-100/70" />
                      <SectionHeading eyebrow="Archive" title="Past sessions" />
                      <div className="space-y-2">
                        {events.past.slice(0, 5).map((event) => (
                          <div
                            key={event.id}
                            className="flex items-center gap-3 rounded-xl border border-primary-100/60 bg-white/70 p-2.5"
                          >
                            {event.image && (
                              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-primary-50">
                                <img
                                  src={event.image.startsWith('http') ? event.image : `${API_BASE_URL}${event.image}`}
                                  alt={event.name}
                                  className="h-full w-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none'
                                  }}
                                />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <h4 className="truncate text-sm font-semibold text-gray-900">{event.name}</h4>
                              <p className="text-xs text-gray-500">
                                {new Date(event.eventDate).toLocaleDateString('en-IN', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })}
                              </p>
                            </div>
                            <span
                              className={cn(
                                'rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize',
                                FORMAT_TONE[event.format] || FORMAT_TONE.online
                              )}
                            >
                              {event.format}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Gallery */}
            <TabsContent value="gallery" className="mt-5">
              <Card className="rounded-2xl border-primary-100/70 bg-white/85 shadow-sm backdrop-blur-sm">
                <CardContent className="p-5 sm:p-6">
                  <SectionHeading
                    eyebrow="In practice"
                    title="Gallery"
                    action={
                      gallery.length > 0 && (
                        <span className="text-[11px] font-medium uppercase tracking-wider text-gray-500">
                          {gallery.filter((g) => g.type === 'image').length} photos ·{' '}
                          {gallery.filter((g) => g.type === 'video').length} videos
                        </span>
                      )
                    }
                  />
                  {gallery.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-primary-200 bg-primary-50/40 p-6 text-center">
                      <Film className="mx-auto mb-2 h-5 w-5 text-primary-600" />
                      <p className="text-sm text-gray-700">No gallery items yet.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
                      {gallery.map((item, i) => {
                        const src = resolveMediaUrl(item.url)
                        return (
                          <motion.button
                            type="button"
                            key={item.id}
                            onClick={() => setLightbox({ ...item, src })}
                            initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.96 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, amount: 0.15 }}
                            whileHover={shouldReduceMotion ? undefined : { y: -2 }}
                            transition={{ duration: 0.35, delay: i * 0.04 }}
                            className={cn(
                              'group relative overflow-hidden rounded-xl border border-primary-100/60 bg-primary-50 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                              i === 0 ? 'col-span-2 row-span-2 aspect-square sm:aspect-[4/3]' : 'aspect-square'
                            )}
                          >
                            {item.type === 'video' ? (
                              <video
                                src={src}
                                muted
                                playsInline
                                preload="metadata"
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                            ) : (
                              <img
                                src={src}
                                alt={item.caption || 'Gallery item'}
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                loading="lazy"
                              />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/0 opacity-90" />
                            {item.type === 'video' && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-primary-800 shadow-lg transition-transform group-hover:scale-110">
                                  <Play className="h-4 w-4 fill-current" />
                                </span>
                              </div>
                            )}
                            {item.caption && (
                              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-2.5">
                                <span className="line-clamp-1 text-left text-[11px] font-medium text-white drop-shadow-sm">
                                  {item.caption}
                                </span>
                                {item.duration && (
                                  <span className="rounded-full bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
                                    {item.duration}
                                  </span>
                                )}
                              </div>
                            )}
                          </motion.button>
                        )
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Testimonials */}
            <TabsContent value="testimonials" className="mt-5">
              <Card className="rounded-2xl border-primary-100/70 bg-white/85 shadow-sm backdrop-blur-sm">
                <CardContent className="p-5 sm:p-6">
                  <SectionHeading eyebrow="In their words" title="What people say" />
                  {testimonials.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-primary-200 bg-primary-50/40 p-6 text-center">
                      <Quote className="mx-auto mb-2 h-5 w-5 text-primary-600" />
                      <p className="text-sm text-gray-700">Nothing here yet.</p>
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {testimonials.map((t, i) => (
                        <motion.figure
                          key={t.id}
                          initial={shouldReduceMotion ? false : { opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, amount: 0.2 }}
                          whileHover={shouldReduceMotion ? undefined : { y: -3 }}
                          transition={{ duration: 0.4, delay: i * 0.06 }}
                          className="relative flex h-full flex-col rounded-xl border border-primary-100/80 bg-white/90 p-4 shadow-sm transition-shadow hover:shadow-md"
                        >
                          <Quote className="absolute right-3 top-3 h-5 w-5 text-primary-200" aria-hidden="true" />
                          <blockquote className="flex-1 text-sm leading-relaxed text-gray-700">
                            &ldquo;{t.quote}&rdquo;
                          </blockquote>
                          <figcaption className="mt-3 flex items-center gap-2 border-t border-primary-100/70 pt-3">
                            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-[11px] font-semibold text-primary-800">
                              {t.authorName?.charAt(0) || '?'}
                            </span>
                            <div className="leading-tight">
                              <div className="text-xs font-semibold text-gray-900">{t.authorName}</div>
                              {t.authorRole && <div className="text-[11px] text-gray-500">{t.authorRole}</div>}
                            </div>
                          </figcaption>
                        </motion.figure>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Resources */}
            <TabsContent value="resources" className="mt-5">
              <Card className="rounded-2xl border-primary-100/70 bg-white/85 shadow-sm backdrop-blur-sm">
                <CardContent className="p-5 sm:p-6">
                  <SectionHeading
                    eyebrow="Take it with you"
                    title="Guides & resources"
                  />
                  {resources.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-primary-200 bg-primary-50/40 p-6 text-center">
                      <FileText className="mx-auto mb-2 h-5 w-5 text-primary-600" />
                      <p className="text-sm text-gray-700">No resources yet.</p>
                    </div>
                  ) : (
                    <div className="grid gap-2.5 sm:grid-cols-2">
                      {resources.map((res, i) => {
                        const meta = RESOURCE_TYPE_META[res.resourceType] || RESOURCE_TYPE_META.link
                        const Icon = meta.icon
                        const isLink = res.resourceType === 'link'
                        return (
                          <motion.a
                            href={resolveMediaUrl(res.fileUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            key={res.id}
                            initial={shouldReduceMotion ? false : { opacity: 0, x: -8 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            whileHover={shouldReduceMotion ? undefined : { y: -2 }}
                            transition={{ duration: 0.35, delay: i * 0.05 }}
                            className="group flex items-start gap-3 rounded-xl border border-primary-100/80 bg-white/85 p-3.5 transition-all hover:border-primary-200 hover:shadow-md"
                          >
                            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700 ring-1 ring-primary-100">
                              <Icon className="h-4 w-4" />
                            </span>
                            <div className="flex-1 leading-tight">
                              <div className="flex items-center justify-between gap-2">
                                <h3 className="text-sm font-semibold text-gray-900">{res.title}</h3>
                                {isLink ? (
                                  <ArrowUpRight className="h-3.5 w-3.5 text-primary-700 transition-transform group-hover:translate-x-0.5" />
                                ) : (
                                  <Download className="h-3.5 w-3.5 text-primary-700 transition-transform group-hover:translate-y-0.5" />
                                )}
                              </div>
                              {res.description && <p className="mt-1 text-xs text-gray-600">{res.description}</p>}
                              <div className="mt-1.5 flex items-center gap-2 text-[11px] text-gray-500">
                                <span>{meta.label}</span>
                                {res.fileSize && (
                                  <>
                                    <span>·</span>
                                    <span>{res.fileSize}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </motion.a>
                        )
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>

        {/* Get in Touch CTA — compact */}
        <motion.section
          initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="mt-6 overflow-hidden rounded-2xl border border-primary-700/30 bg-gradient-to-br from-primary-700 via-primary-700 to-primary-800 p-5 text-cream shadow-md sm:p-6"
        >
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row sm:gap-6">
            <div className="text-center sm:text-left">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-200">
                Ready to begin?
              </p>
              <h2 className="mt-1 text-xl font-semibold leading-tight sm:text-2xl">
                Reach out to {facilitator.name?.split(' ')[0]}
              </h2>
              <p className="mt-1 text-sm text-primary-100/90">
                A simple message can be the start of a new rhythm.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {facilitator.instagramId ? (
                <Button
                  asChild
                  className="h-10 rounded-full bg-cream px-5 text-xs font-semibold text-primary-800 shadow-sm hover:bg-white"
                >
                  <a
                    href={`https://instagram.com/${facilitator.instagramId.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Instagram className="h-3.5 w-3.5" />
                    Connect on Instagram
                  </a>
                </Button>
              ) : facilitator.website ? (
                <Button
                  asChild
                  className="h-10 rounded-full bg-cream px-5 text-xs font-semibold text-primary-800 shadow-sm hover:bg-white"
                >
                  <a
                    href={facilitator.website.startsWith('http') ? facilitator.website : `https://${facilitator.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Globe className="h-3.5 w-3.5" />
                    Visit website
                  </a>
                </Button>
              ) : (
                <Button
                  asChild
                  className="h-10 rounded-full bg-cream px-5 text-xs font-semibold text-primary-800 shadow-sm hover:bg-white"
                >
                  <Link to="/facilitators">All facilitators</Link>
                </Button>
              )}
              <Button
                onClick={() => setActiveTab('classes')}
                variant="outline"
                className="h-10 rounded-full border-cream/30 bg-transparent px-5 text-xs font-semibold text-cream hover:bg-cream/10 hover:text-cream"
              >
                Browse offerings
              </Button>
            </div>
          </div>
        </motion.section>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setLightbox(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="relative max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              {lightbox.type === 'video' ? (
                <video
                  src={lightbox.src}
                  controls
                  autoPlay
                  className="max-h-[80vh] w-auto rounded-2xl shadow-2xl"
                />
              ) : (
                <img
                  src={lightbox.src}
                  alt={lightbox.caption || 'Gallery item'}
                  className="max-h-[80vh] w-auto rounded-2xl shadow-2xl"
                />
              )}
              <div className="mt-3 flex items-center justify-between gap-3 text-cream">
                <p className="text-sm">{lightbox.caption}</p>
                <button
                  onClick={() => setLightbox(null)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm transition-colors hover:bg-white/25"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Interest Form Dialog */}
      <Dialog
        open={Boolean(selectedListing)}
        onOpenChange={(open) => {
          if (!open) closeInterestModal()
        }}
      >
        <DialogContent className="max-w-lg gap-0 rounded-2xl border-primary-100 bg-white p-5 shadow-xl sm:p-6">
          <DialogHeader className="mb-4">
            <DialogTitle className="text-lg font-semibold text-gray-900">Express interest</DialogTitle>
          </DialogHeader>

          {selectedListing && (
            <div className="mb-5 rounded-xl border border-primary-100/80 bg-primary-50/70 p-3.5">
              <h3 className="text-sm font-semibold text-primary-900">{selectedListing.title}</h3>
              <div className="mt-1 flex flex-wrap gap-1.5 text-[11px] text-primary-700">
                {selectedListing.format && <span className="capitalize">{selectedListing.format}</span>}
                {selectedListing.duration && <span>· {selectedListing.duration}</span>}
                {selectedListing.fee && <span>· {selectedListing.fee}</span>}
              </div>
            </div>
          )}

          {interestSuccess ? (
            <div className="py-6 text-center">
              <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <BadgeCheck className="h-6 w-6" />
              </div>
              <h3 className="mb-1 text-base font-semibold text-gray-900">Interest submitted!</h3>
              <p className="mb-5 text-sm text-gray-600">
                {facilitator.name?.split(' ')[0]} will reach out to you soon.
              </p>
              <DialogClose asChild>
                <Button className="h-10 rounded-full bg-primary-700 px-6 text-sm font-semibold hover:bg-primary-800">
                  Done
                </Button>
              </DialogClose>
            </div>
          ) : (
            <form onSubmit={handleInterestSubmit} className="space-y-3.5">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={interestForm.name}
                  onChange={(e) => setInterestForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded-xl border border-primary-100 bg-white px-3.5 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={interestForm.email}
                  onChange={(e) => setInterestForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full rounded-xl border border-primary-100 bg-white px-3.5 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  value={interestForm.phone}
                  onChange={(e) => setInterestForm((prev) => ({ ...prev, phone: e.target.value }))}
                  className="w-full rounded-xl border border-primary-100 bg-white px-3.5 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                  placeholder="Your phone number"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Message</label>
                <textarea
                  rows={3}
                  value={interestForm.message}
                  onChange={(e) => setInterestForm((prev) => ({ ...prev, message: e.target.value }))}
                  className="w-full resize-none rounded-xl border border-primary-100 bg-white px-3.5 py-2.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                  placeholder="Any questions or specific needs?"
                />
              </div>

              {interestError && <p className="text-xs text-red-600">{interestError}</p>}

              <Button
                type="submit"
                disabled={submittingInterest}
                className="h-11 w-full rounded-full bg-primary-700 text-sm font-semibold hover:bg-primary-800 disabled:opacity-50"
              >
                {submittingInterest ? 'Submitting…' : (
                  <>
                    <Send className="h-3.5 w-3.5" />
                    Submit interest
                  </>
                )}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default FacilitatorDetail
